import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { API_BASE } from "../api";
import SearchBar from "./SearchBar.js";
import ChatList from "./ChatList.js";
import { useAuth } from "../context/AuthContext.js";
import { socket } from "../socket";
import GroupCreateModal from "./GroupCreateModal";
import JoinGroupModal from "./JoinGroupModal";
import ProfileModal from "./ProfileModal";
import BlockedList from "./BlockedList"; // ✅ Added
import CallHistory from "./CallHistory"; // ✅ Added
import BusinessRegistrationModal from "./BusinessRegistrationModal"; // ✅ Business

import CommunityCreateModal from "./CommunityCreateModal"; // ✅ Community
import CommunityManageModal from "./CommunityManageModal"; // ✅ Community
import NumberSearchSection from "./NumberSearchSection"; // ✅ New Search Section
import CreateContactSection from "./CreateContactSection"; // ✅ New Contact Section
import NewChatSection from "./NewChatSection"; // ✅ New Chat Section
import NavRail from "./NavRail";
import { requestNotificationPermission, unsubscribeFromNotifications } from "../utils/notificationHelper";

export default function Sidebar({ onOpenChat, activeChatId, onViewStatus, onViewMyBusiness }) {
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openJoin, setOpenJoin] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const [openBusinessReg, setOpenBusinessReg] = useState(false); // ✅ Business
  const [openCommunityCreate, setOpenCommunityCreate] = useState(false); // ✅ Community
  const [openCommunityManage, setOpenCommunityManage] = useState(false); // ✅ Community
  // const [openNumberSearch, setOpenNumberSearch] = useState(false); // ❌ Removed for Section
  // const [openCreateContact, setOpenCreateContact] = useState(false); // ❌ Removed for Section

  const [sidebarView, setSidebarView] = useState('default'); // 'default' | 'number-search' | 'create-contact'

  const [showPlusMenu, setShowPlusMenu] = useState(false); // ✅ Plus Menu Logic
  const [viewingCommunity, setViewingCommunity] = useState(null); // ✅ Community ID when drilling down
  const [communityDetails, setCommunityDetails] = useState(null); // ✅ Community Data
  const [activeTab, setActiveTab] = useState("chats"); // chats, groups, calls, status, settings
  const [notifSettings, setNotifSettings] = useState(() =>
    JSON.parse(localStorage.getItem("notificationSettings") || '{"sound":true,"push":true}')
  );
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null); // ✅ Ref for menu
  const plusMenuRef = useRef(null); // ✅ Ref for plus menu
  const [chatFilter, setChatFilter] = useState('all'); // ✅ Filter State

  // ✅ Close menu on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
      if (plusMenuRef.current && !plusMenuRef.current.contains(event.target)) { // ✅ Close plus menu
        setShowPlusMenu(false);
      }
    };

    if (showMenu || showPlusMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu, showPlusMenu]);

  const handleMarkAllRead = () => {
    setChats((prev) => prev.map((c) => ({ ...c, unread: 0 })));
    chats.forEach(chat => {
      if (chat.unread > 0) {
        socket.emit("message:readAll", { chatId: chat.id });
      }
    });
    setShowMenu(false);
  };

  // ✅ Social Tab State
  const [socialView, setSocialView] = useState('apps'); // 'apps' | 'contacts'
  const [selectedApp, setSelectedApp] = useState(null); // { id: 'ecommerce', name: '...' }
  const [connectedApps, setConnectedApps] = useState([]);

  // ✅ Fetch Connected Apps
  useEffect(() => {
    const fetchApps = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/integration/apps`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        if (data.success) {
          setConnectedApps(data.apps);
        }
      } catch (e) { console.error("Failed to fetch connected apps", e); }
    };
    fetchApps();
  }, [user.token]);

  const toggleSetting = async (key) => {
    const newVal = !notifSettings[key];
    const newSettings = { ...notifSettings, [key]: newVal };
    setNotifSettings(newSettings);
    localStorage.setItem("notificationSettings", JSON.stringify(newSettings));

    if (key === "push") {
      if (newVal) {
        await requestNotificationPermission(user.token);
      } else {
        await unsubscribeFromNotifications(user.token);
      }
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/chats`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      setChats(data);
    } catch (err) {
      console.error("Failed to fetch chats:", err);
    }
  }, [user?.token]);

  // ✅ Community: Load Communities
  const [communities, setCommunities] = useState([]);
  const loadCommunities = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE}/communities`, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });
      setCommunities(data);
    } catch (e) { console.error("Failed to load communities", e); }
  }, [user?.token]);

  // ✅ Community: Load Specific Community Details
  const loadCommunityDetails = useCallback(async (communityId) => {
    try {
      const { data } = await axios.get(`${API_BASE}/communities/${communityId}`, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });
      setCommunityDetails(data);
    } catch (e) {
      console.error("Failed to load community details", e);
      setViewingCommunity(null); // fallback
    }
  }, [user?.token]);

  useEffect(() => {
    if (activeTab === 'communities' && !viewingCommunity) {
      loadCommunities();
    }
    if (viewingCommunity) {
      loadCommunityDetails(viewingCommunity);
    }
  }, [activeTab, viewingCommunity, loadCommunities, loadCommunityDetails]);

  useEffect(() => {
    const fetchChatsOnce = async () => {
      if (!user?.token) return;
      try {
        const { data } = await axios.get(`${API_BASE}/chats`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
        setChats((prev) => {
          const prevMap = new Map(prev.map(c => [c.id, c]));
          const merged = data.map(c => {
            const local = prevMap.get(c.id);
            return {
              ...c,
              unread: Math.max(c.unread || 0, local?.unread || 0)
            };
          });

          if (window.__initialChatId) {
            const match = merged.find(c => String(c.id) === String(window.__initialChatId));
            if (match) {
              onOpenChat(match);
              window.__initialChatId = null;
            }
          }
          return merged;
        });
      } catch (err) {
        console.error("Failed to fetch chats:", err);
      }
    };
    fetchChatsOnce();
  }, [user, onOpenChat]);

  const fetchChat = useCallback(async (chatId) => {
    try {
      const { data } = await axios.get(`${API_BASE}/chats/${chatId}`, {
        headers: { Authorization: `Bearer ${user?.token}` },
      });
      setChats((prev) => {
        const exists = prev.some(c => c.id === data.id);
        if (exists) return prev;
        return sortChats([data, ...prev], user?.id);
      });
    } catch (err) {
      console.error("Failed to fetch new chat:", err);
    }
  }, [user?.token, user?.id]);

  useEffect(() => {
    const onNewMessage = (msg) => {
      setChats((prev) => {
        const chatExists = prev.some(c => String(c.id) === String(msg.chat));

        if (!chatExists) {
          // Discover new chat!
          fetchChat(msg.chat);
          return prev;
        }

        const updated = prev.map((c) => {
          if (String(c.id) === String(msg.chat)) {
            return {
              ...c,
              lastMessage: (() => {
                let lastMsg = msg.body;
                if (msg.type && msg.type !== 'text') {
                  if (msg.type === 'image') lastMsg = "Photo";
                  else if (msg.type === 'video') lastMsg = "Video";
                  else if (msg.type === 'audio') lastMsg = "Audio";
                  else if (msg.type === 'file' || msg.type === 'document') {
                    lastMsg = msg.body && !msg.body.startsWith('http') ? msg.body : "File";
                  } else {
                    lastMsg = msg.type ? (msg.type.charAt(0).toUpperCase() + msg.type.slice(1)) : "Attachment";
                  }
                } else if (msg.attachments && msg.attachments.length > 0) {
                  const att = msg.attachments[0];
                  if (att.type === 'image') lastMsg = "Photo";
                  else if (att.type === 'video') lastMsg = "Video";
                  else lastMsg = "File";
                }
                return lastMsg;
              })(),
              lastAt: msg.createdAt,
              unread: (String(msg.sender) !== String(user?.id) && String(msg.sender?._id) !== String(user?.id) && String(msg.chat) !== String(activeChatId))
                ? (c.unread || 0) + 1
                : c.unread
            };
          }
          return c;
        });
        return sortChats(updated, user?.id);
      });
    };

    socket.on("message:new", onNewMessage);
    return () => socket.off("message:new", onNewMessage);
  }, [user?.id, activeChatId, fetchChat]);

  // ✅ Auto-clear unread for active chat
  useEffect(() => {
    if (activeChatId) {
      setChats((prev) =>
        prev.map((c) => (c.id === activeChatId ? { ...c, unread: 0 } : c))
      );
    }
  }, [activeChatId]);

  useEffect(() => {
    const onUnreadReset = ({ chatId, unreadResetFor }) => {
      if (!user) return;
      if (unreadResetFor !== user.id) return;
      setChats((prev) =>
        prev.map((c) => (c.id === chatId ? { ...c, unread: 0 } : c))
      );
    };

    socket.on("chats:update", onUnreadReset);
    return () => socket.off("chats:update", onUnreadReset);
  }, [user]);

  useEffect(() => {
    const onChatPinned = ({ chatId }) => {
      setChats((prev) => {
        const updated = prev.map((c) =>
          c.id === chatId ? { ...c, isPinned: true } : c
        );
        return sortChats(updated, user?.id);
      });
    };

    const onChatUnpinned = ({ chatId }) => {
      setChats((prev) => {
        const updated = prev.map((c) =>
          c.id === chatId ? { ...c, isPinned: false } : c
        );
        return sortChats(updated, user?.id);
      });
    };

    const handleRefresh = () => load();
    window.addEventListener("chats:refresh", handleRefresh);

    socket.on("chat:pinned", onChatPinned);
    socket.on("chat:unpinned", onChatUnpinned);
    return () => {
      socket.off("chat:pinned", onChatPinned);
      socket.off("chat:unpinned", onChatUnpinned);
      window.removeEventListener("chats:refresh", handleRefresh);
    };
  }, [user?.id, load]);

  const sortChats = (chatList, userId) => {
    return [...chatList].sort((a, b) => {
      // 2. Pinned chats next
      const aIsPinned = a.isPinned || a.pinnedBy?.includes(userId);
      const bIsPinned = b.isPinned || b.pinnedBy?.includes(userId);
      if (aIsPinned && !bIsPinned) return -1;
      if (!aIsPinned && bIsPinned) return 1;

      // 3. Favorites next
      if (a.other?.isFavorite && !b.other?.isFavorite) return -1;
      if (!a.other?.isFavorite && b.other?.isFavorite) return 1;

      // 4. Newest first
      const dateA = new Date(a.lastAt || a.updatedAt || a.createdAt);
      const dateB = new Date(b.lastAt || b.updatedAt || b.createdAt);
      return dateB - dateA;
    });
  };

  const filteredChats = chats.filter(c => {
    if (activeTab === "groups") return c.isGroup && !c.isArchived && !c.isAnnouncementGroup;
    if (activeTab === "favorites") return !c.isGroup && c.other?.isFavorite && !c.isArchived;
    // ✅ Social Tab: In 'contacts' view, filter by selected app
    if (activeTab === "social") {
      if (socialView === 'contacts' && selectedApp) {
        return c.origin === selectedApp.chatOriginId && !c.isArchived;
      }
      return false; // In 'apps' view, we don't show chats directly
    }
    // ✅ Main Chats Tab with Filters
    if (activeTab === "chats") {
      const baseMatches = !c.isArchived && c.origin !== 'ecommerce';
      if (!baseMatches) return false;

      if (chatFilter === 'unread') return c.unread > 0;
      if (chatFilter === 'groups') return c.isGroup;
      if (chatFilter === 'favorites') return c.other?.isFavorite;
      return true;
    }
    if (activeTab === "archived") return c.isArchived;
    return true; // fallback for others
  });

  // ✅ "Me" chat removed as requested
  let displayChats = filteredChats;

  const renderContent = () => {
    // ✅ New Views for embedded sections
    if (sidebarView === 'number-search') {
      return (
        <div className="flex-1 flex flex-col h-full bg-[#0f172a]">
          <NumberSearchSection
            onBack={() => setSidebarView('new-chat')}
            onOpenChat={(chat) => {
              setChats(prev => {
                const exists = prev.find(c => c.id === chat.id);
                if (exists) return prev;
                return [chat, ...prev];
              });
              onOpenChat(chat);
              setSidebarView('default');
            }}
          />
        </div>
      );
    }
    if (sidebarView === 'create-contact') {
      return (
        <div className="flex-1 flex flex-col h-full bg-[#0f172a]">
          <CreateContactSection
            onBack={() => setSidebarView('new-chat')}
            onOpenChat={(chat) => {
              setChats(prev => {
                const exists = prev.find(c => c.id === chat.id);
                if (exists) return prev;
                return [chat, ...prev];
              });
              onOpenChat(chat);
              setSidebarView('default');
            }}
          />
        </div>
      );
    }

    if (sidebarView === 'new-chat') {
      return (
        <div className="flex-1 flex flex-col h-full bg-[#0f172a]">
          <NewChatSection
            onBack={() => setSidebarView('default')}
            onNewGroup={() => { setOpenCreate(true); }}
            onNewContact={() => setSidebarView('create-contact')}
            onNewCommunity={() => { setOpenCommunityCreate(true); }}
            onDialNumber={() => setSidebarView('number-search')}
            onOpenChat={onOpenChat}
          />
        </div>
      );
    }

    if (activeTab === "settings") {
      return (
        <div className="flex-1 p-6 space-y-6 overflow-y-auto custom-scrollbar">
          <h2 className="text-2xl font-bold mb-4">Settings</h2>
          <div className="space-y-4">
            <button
              onClick={() => setOpenProfile(true)}
              className="w-full flex items-center justify-between p-4 bg-white/60 hover:bg-white/80 rounded-2xl transition-all shadow-sm border border-white/40 text-slate-800"
            >
              <span>Account Information</span>
              <svg className="w-5 h-5 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" /></svg>
            </button>

            {/* About and Email Fields */}
            <div className="p-4 bg-white/60 rounded-2xl space-y-4 shadow-sm border border-white/40 text-slate-800">
              <div className="space-y-2">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-widest">About</label>
                <textarea
                  defaultValue={user?.about || ""}
                  onBlur={async (e) => {
                    const newAbout = e.target.value;
                    try {
                      await axios.put(
                        `${API_BASE}/users/profile`,
                        { about: newAbout },
                        { headers: { Authorization: `Bearer ${user.token}` } }
                      );
                      const updatedUser = { ...user, about: newAbout };
                      localStorage.setItem("auth_user", JSON.stringify(updatedUser));
                    } catch (err) {
                      console.error("Failed to update about:", err);
                      alert("Failed to update about");
                    }
                  }}
                  rows={3}
                  className="w-full bg-white/80 text-slate-800 text-sm rounded-lg px-3 py-2 border border-white/20 outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                  placeholder="Write something about yourself..."
                />
              </div>
              <div className="space-y-2 border-t border-white/20 pt-4">
                <label className="text-xs font-bold text-slate-600 uppercase tracking-widest">Email</label>
                <input
                  type="email"
                  defaultValue={user?.email || ""}
                  onBlur={async (e) => {
                    const newEmail = e.target.value;
                    try {
                      await axios.put(
                        `${API_BASE}/users/profile`,
                        { email: newEmail },
                        { headers: { Authorization: `Bearer ${user.token}` } }
                      );
                      const updatedUser = { ...user, email: newEmail };
                      localStorage.setItem("auth_user", JSON.stringify(updatedUser));
                    } catch (err) {
                      console.error("Failed to update email:", err);
                      alert("Failed to update email");
                    }
                  }}
                  className="w-full bg-white/80 text-slate-800 text-sm rounded-lg px-3 py-2 border border-white/20 outline-none focus:ring-2 focus:ring-primary/50"
                  placeholder="your.email@example.com"
                />
              </div>
            </div>

            {/* My Business Button - Removed from here, now in main nav */}


            {/* Business Registration Button - Only show if user doesn't have a business */}
            {!user?.isBusiness && (
              <button
                onClick={() => setOpenBusinessReg(true)}
                className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/10 hover:from-green-500/20 hover:to-emerald-500/20 rounded-2xl transition-all border border-green-500/20"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <div className="font-bold text-green-500">Register Business Account</div>
                    <div className="text-[10px] opacity-60 uppercase tracking-widest font-black">Start selling with catalog</div>
                  </div>
                </div>
                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" /></svg>
              </button>
            )}

            <div className="p-4 bg-white/60 rounded-2xl space-y-4 shadow-sm border border-white/40 text-slate-800">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold">Sound Notifications</div>
                  <div className="text-[10px] opacity-40 uppercase tracking-widest font-black">Incoming messages</div>
                </div>
                <button
                  onClick={() => toggleSetting("sound")}
                  className={`w-12 h-6 rounded-full transition-all relative ${notifSettings.sound ? "bg-primary" : "bg-white/10"}`}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${notifSettings.sound ? "right-1" : "left-1"}`} />
                </button>
              </div>
              <div className="flex items-center justify-between border-t border-white/5 pt-4">
                <div>
                  <div className="font-bold">Push Notifications</div>
                  <div className="text-[10px] opacity-40 uppercase tracking-widest font-black">Browser popups</div>
                </div>
                <button
                  onClick={() => toggleSetting("push")}
                  className={`w-12 h-6 rounded-full transition-all relative ${notifSettings.push ? "bg-primary" : "bg-white/10"}`}
                >
                  <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${notifSettings.push ? "right-1" : "left-1"}`} />
                </button>
              </div>
            </div>

            <div className="p-4 bg-white/60 rounded-2xl space-y-4 shadow-sm border border-white/40 text-slate-800">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold text-emerald-400">Disappearing Messages</div>
                  <div className="text-[10px] opacity-40 uppercase tracking-widest font-black">Personal retention policy</div>
                </div>
                <select
                  value={user?.messageRetentionDays || 0}
                  onChange={async (e) => {
                    const days = parseInt(e.target.value);
                    try {
                      await axios.patch(`${API_BASE}/users/retention`, { days }, {
                        headers: { Authorization: `Bearer ${user.token}` }
                      });
                      // Update local user state if possible, or just refresh
                      window.location.reload(); // Quickest way to sync AuthContext user
                    } catch (err) {
                      alert("Failed to update retention policy");
                    }
                  }}
                  className="bg-white/80 text-[#0f172a] text-xs font-bold py-1 px-2 rounded-lg border border-white/20 outline-none focus:ring-1 focus:ring-primary shadow-sm"
                >
                  <option value={0}>Off</option>
                  <option value={1}>24 Hours</option>
                  <option value={7}>7 Days</option>
                  <option value={30}>30 Days</option>
                </select>
              </div>
            </div>

            {/* 
            <div className="p-4 bg-white/5 rounded-2xl space-y-3">
              <div className="flex justify-between items-center opacity-70">
                <span>Theme</span>
                <span className="text-primary font-bold">Premium Dark</span>
              </div>
              <div className="flex justify-between items-center opacity-70">
                <span>Encrypted Chats</span>
                <span className="text-emerald-400 font-bold">Enabled</span>
              </div>
            </div>
            */}
          </div>
        </div>
      );
    }

    if (activeTab === "calls") {
      return (
        <CallHistory onStartCall={(peerId, type) => {
          // Find the chat for this peer and trigger the call
          const chat = chats.find(c => !c.isGroup && c.other?.id === peerId);
          if (chat) {
            onOpenChat(chat);
            // We might need a way to trigger the call directly from Sidebar
            // For now, opening the chat is a good start as it shows the call buttons
          }
        }} />
      );
    }

    if (activeTab === "blocked") {
      return <BlockedList />;
    }

    // ✅ Communities Tab
    if (activeTab === "communities") {
      if (viewingCommunity && communityDetails) {
        // Drill Down View
        return (
          <div className="flex-1 flex flex-col h-full bg-transparent">
            {/* Header */}
            <div className="p-4 border-b border-white/20 flex items-center gap-4 sticky top-0 bg-white/60 backdrop-blur-md z-10 animate-fade-in shadow-sm">
              <button
                onClick={() => { setViewingCommunity(null); setCommunityDetails(null); }}
                className="w-10 h-10 flex items-center justify-center bg-white/40 hover:bg-white/60 rounded-xl transition-all hover:scale-105 active:scale-95 text-slate-600 hover:text-slate-900 shadow-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" /></svg>
              </button>
              <div className="flex-1 min-w-0">
                <h2 className="font-black text-xl leading-tight truncate text-slate-900 tracking-tight">{communityDetails.name}</h2>
                <p className="text-[10px] text-secondary font-bold uppercase tracking-widest mt-0.5">Community</p>
              </div>
              {communityDetails.admins?.includes(user?.id) && (
                <button
                  onClick={() => setOpenCommunityManage(true)}
                  className="w-10 h-10 flex items-center justify-center bg-primary/10 hover:bg-primary/20 text-primary rounded-xl transition-all hover:scale-105 active:scale-95"
                  title="Manage Community"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-3">
              {/* Announcement Group Card */}
              {communityDetails.announcementGroup && (
                <div
                  onClick={() => onOpenChat(communityDetails.announcementGroup)}
                  className="relative group p-4 rounded-2xl cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 border border-white/5 hover:border-primary/30"
                >
                  {/* Gradient Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0f172a] to-[#1e293b] z-0" />
                  <div className="absolute inset-0 bg-primary/5 group-hover:bg-primary/10 transition-colors z-0" />

                  {/* Icon & Glitter */}
                  <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                    <svg className="w-24 h-24 text-primary" fill="currentColor" viewBox="0 0 24 24"><path d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /></svg>
                  </div>

                  <div className="relative z-10 flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-white shadow-lg shadow-primary/20 overflow-hidden transform group-hover:rotate-6 transition-transform">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" /></svg>
                    </div>
                    <div className="flex-1">
                      <div className="font-black text-lg text-white group-hover:text-primary-light transition-colors">Announcements</div>
                      <div className="text-xs font-medium text-white/50 group-hover:text-white/70 transition-colors">Official Community Updates</div>
                    </div>
                    <svg className="w-5 h-5 text-white/20 group-hover:text-primary-light transition-all transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" /></svg>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mt-6 mb-3 px-1">
                <div className="text-xs font-bold text-white/40 uppercase tracking-widest">Groups</div>
                <div className="text-[10px] font-bold bg-white/5 text-white/40 px-2 py-0.5 rounded-full">{communityDetails.groups?.length || 0}</div>
              </div>

              <div className="space-y-1">
                {communityDetails.groups?.map(group => (
                  <div
                    key={group.id}
                    onClick={() => group.isMember ? onOpenChat(group) : alert("You are not a member of this group.")}
                    className={`flex items-center gap-4 p-3 rounded-2xl transition-all border border-transparent ${group.isMember
                      ? 'hover:bg-white/5 hover:border-white/5 cursor-pointer group active:scale-[0.99]'
                      : 'opacity-50 grayscale cursor-not-allowed'
                      }`}
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-xl flex items-center justify-center font-bold text-white border border-white/5 group-hover:border-white/10 group-hover:shadow-[0_0_15px_rgba(99,102,241,0.2)] transition-all">
                      {group.title?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-gray-200 group-hover:text-white truncate transition-colors">{group.title}</div>
                      <div className="text-xs text-gray-500 group-hover:text-gray-400 flex items-center gap-1.5">
                        <span>{group.participantsCount} members</span>
                        {group.isMember && (
                          <>
                            <span className="w-1 h-1 rounded-full bg-emerald-500/50"></span>
                            <span className="text-emerald-500 font-medium">Joined</span>
                          </>
                        )}
                      </div>
                    </div>
                    {group.isMember && (
                      <svg className="w-5 h-5 text-white/10 group-hover:text-white/30 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" /></svg>
                    )}
                  </div>
                ))}

                {communityDetails.groups?.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-10 opacity-40">
                    <svg className="w-12 h-12 mb-3 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" /></svg>
                    <div className="text-sm font-medium">No groups yet</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }

      // List View
      return (
        <div className="flex-1 flex flex-col h-full">
          <div className="px-5 pt-6 pb-2 flex justify-between items-center">
            <h2 className="text-2xl font-black">Communities</h2>
            <button onClick={() => setOpenCommunityCreate(true)} className="w-8 h-8 flex items-center justify-center bg-secondary text-white rounded-lg hover:bg-secondary-dark transition-colors shadow-lg shadow-secondary/20" title="Create Community">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" /></svg>
            </button>
          </div>

          <div className="px-5 pb-4">
            <p className="text-xs text-gray-400">Communities bring members together in topic-based groups.</p>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-2">
            {communities.map(comm => (
              <div
                key={comm.id}
                onClick={() => setViewingCommunity(comm.id)}
                className="flex items-center gap-4 p-4 hover:bg-white/5 rounded-2xl cursor-pointer transition-all border border-transparent hover:border-white/5"
              >
                <div className="relative">
                  <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center text-indigo-400">
                    {comm.icon ? <img src={comm.icon} className="w-full h-full object-cover rounded-xl" alt="" /> : <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" /></svg>}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="font-bold text-base text-gray-200">{comm.name}</div>
                  <div className="text-xs text-gray-500 truncate">{comm.description || "No description"}</div>
                </div>
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" /></svg>
              </div>
            ))}

            {communities.length === 0 && (
              <div className="flex flex-col items-center justify-center py-10 opacity-50">
                <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                </div>
                <p className="text-sm font-bold">No communities</p>
                <p className="text-xs">Create one to get started!</p>
              </div>
            )}
          </div>
        </div>
      );
    }

    const getTitle = () => {
      if (activeTab === 'groups') return 'Groups';
      if (activeTab === 'favorites') return 'Favorites';
      if (activeTab === 'social') return socialView === 'contacts' ? selectedApp?.name : 'Social Apps'; // ✅ Dynamic Title
      if (activeTab === 'archived') return 'Archived';
      return 'Chats';
    };

    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header Area */}
        <div className="px-5 pt-6 pb-2">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {/* ✅ Back Button for Social Contacts View */}
              {activeTab === 'social' && socialView === 'contacts' && (
                <button
                  onClick={() => { setSocialView('apps'); setSelectedApp(null); }}
                  className="p-2 -ml-2 rounded-full hover:bg-white/20 text-slate-500 hover:text-slate-800 transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" /></svg>
                </button>
              )}
              <h2 className="text-2xl font-black">{getTitle()}</h2>
            </div>
            <div className="flex gap-2 items-center">
              {activeTab === 'groups' && (
                <>
                  <button
                    onClick={() => setOpenJoin(true)}
                    className="h-8 px-3 flex items-center justify-center bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-xs font-bold ring-1 ring-white/10"
                    title="Join group with code"
                  >
                    Join
                  </button>
                </>
              )}

              {/* Plus Menu - Refactored to Full Page View */}
              <div className="relative z-50">
                <button
                  onClick={() => setSidebarView('new-chat')}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors text-slate-600 bg-secondary/10 text-secondary"
                  title="New Chat"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
                </button>
              </div>

              {/* Three Dot Menu */}
              <div className="relative z-50" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-3 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-all relative group"
                  title="More Options"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" /></svg>
                </button>
                {showMenu && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white/90 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl overflow-hidden py-1 animate-fade-in origin-top-right z-[100]">
                    <button onClick={() => { setOpenCreate(true); setShowMenu(false); }} className="w-full text-left px-4 py-3 hover:bg-black/5 text-sm font-medium flex items-center gap-3 text-slate-700 transition-colors">
                      <svg className="w-5 h-5 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                      New Group
                    </button>
                    <button onClick={() => { setOpenCommunityCreate(true); setShowMenu(false); }} className="w-full text-left px-4 py-3 hover:bg-black/5 text-sm font-medium flex items-center gap-3 text-slate-700 transition-colors">
                      <svg className="w-5 h-5 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                      New Community
                    </button>
                    <button onClick={() => { setActiveTab('settings'); setShowMenu(false); onOpenChat(null); }} className="w-full text-left px-4 py-3 hover:bg-black/5 text-sm font-medium flex items-center gap-3 text-slate-700 transition-colors">
                      <svg className="w-5 h-5 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      Settings
                    </button>
                    <div className="h-px bg-black/5 my-1" />
                    <button onClick={handleMarkAllRead} className="w-full text-left px-4 py-3 hover:bg-primary/5 text-sm font-medium flex items-center gap-3 text-primary transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      Mark all as read
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <SearchBar onOpen={async (chat) => { onOpenChat(chat); await load(); }} />

          {/* ✅ Filter Chips */}
          {activeTab === 'chats' && (
            <div className="flex gap-2 overflow-x-auto no-scrollbar mt-3 pb-1">
              {['all', 'unread', 'groups', 'favorites'].map(f => (
                <button
                  key={f}
                  onClick={() => setChatFilter(f)}
                  className={`px-3 py-1 rounded-full text-xs font-bold transition-all whitespace-nowrap ${chatFilter === f
                    ? "bg-slate-900 text-white shadow-md"
                    : "bg-slate-100/50 text-slate-500 hover:bg-white hover:text-slate-800 border border-transparent hover:border-slate-200"
                    }`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* List Container */}
        <div className="flex-1 overflow-y-auto custom-scrollbar pt-2">

          {/* ✅ Social App List View */}
          {activeTab === 'social' && socialView === 'apps' ? (
            <div className="px-3 space-y-2">
              {connectedApps.map(app => {
                // Count unread for this app
                const unread = chats.filter(c => c.origin === app.chatOriginId).reduce((acc, c) => acc + (c.unread || 0), 0);

                return (
                  <div
                    key={app._id}
                    onClick={() => { setSelectedApp(app); setSocialView('contacts'); }}
                    className="flex items-center gap-4 p-4 hover:bg-white/5 rounded-2xl cursor-pointer transition-all border border-transparent hover:border-white/5 group"
                  >
                    <div className="relative">
                      <div className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center text-white border border-white/5 overflow-hidden">
                        {app.icon ? <img src={app.icon} className="w-full h-full object-cover" alt="" /> : (
                          <svg className="w-6 h-6 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" /></svg>
                        )}
                      </div>
                      {unread > 0 && <span className="absolute -top-1 -right-1 bg-primary text-white text-[10px] font-bold px-1.5 h-4 flex items-center justify-center rounded-full border border-[#040712]">{unread}</span>}
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-base text-gray-200 group-hover:text-white transition-colors">{app.name}</div>
                      <div className="text-xs text-gray-500">Connected App</div>
                    </div>
                    <svg className="w-5 h-5 text-gray-700 group-hover:text-white/30 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" /></svg>
                  </div>
                );
              })}
              {connectedApps.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 opacity-40">
                  <div className="text-sm">No connected apps</div>
                </div>
              )}
            </div>
          ) : (
            <ChatList
              items={sortChats(displayChats, user?.id)}
              activeId={activeChatId}
              userId={user?.id}
              onOpen={async (chat) => {
                if (chat.isSynthetic) {
                  try {
                    const { data } = await axios.post(`${API_BASE}/chats/open`,
                      { targetPhone: user.phone },
                      { headers: { Authorization: `Bearer ${user.token}` } }
                    );
                    onOpenChat(data);
                    await load();
                  } catch (e) { console.error("Self-chat init failed", e); }
                  return;
                }
                onOpenChat(chat);
                setChats((prev) => prev.map((c) => (c.id === chat.id ? { ...c, unread: 0 } : c)));
              }}
            />
          )}

        </div>
      </div >
    );
  };

  return (
    <div className="flex flex-col-reverse md:flex-row h-full overflow-hidden glass-panel border-r border-white/40">
      <NavRail
        activeTab={activeTab}
        onTabChange={(id) => {
          if (id === 'status') {
            onViewStatus();
          } else if (id === 'my-business') {
            onViewMyBusiness?.();
          } else if (id === 'ai-assistant') {
            onOpenChat({ id: 'benly', name: 'Benly AI', isAI: true, avatar: null });
            setActiveTab(id);
          } else {
            setActiveTab(id);
            if (id === 'settings') {
              onOpenChat(null);
            }
          }
        }}
        onOpenProfile={() => setOpenProfile(true)}
      />

      <div className="flex-1 flex flex-col h-full overflow-hidden pb-[64px] md:pb-0">
        {renderContent()}
      </div>

      <ProfileModal open={openProfile} onClose={() => setOpenProfile(false)} />
      <GroupCreateModal open={openCreate} onClose={() => setOpenCreate(false)} onCreated={async () => { setOpenCreate(false); await load(); }} />
      <JoinGroupModal open={openJoin} onClose={() => setOpenJoin(false)} onRefresh={load} />
      <BusinessRegistrationModal
        open={openBusinessReg}
        onClose={() => setOpenBusinessReg(false)}
        onSuccess={async () => { setOpenBusinessReg(false); await load(); }}
      />
      <CommunityCreateModal
        open={openCommunityCreate}
        onClose={() => setOpenCommunityCreate(false)}
        onCreated={() => { loadCommunities(); }}
      />
      {viewingCommunity && (
        <CommunityManageModal
          open={openCommunityManage}
          onClose={() => setOpenCommunityManage(false)}
          communityId={viewingCommunity}
        />
      )}
    </div>
  );
}
